import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit2, 
  User, 
  Building, 
  Phone, 
  Mail, 
  Calendar, 
  DollarSign, 
  Target, 
  Star, 
  
  FileText,
  Award,
  Users,
  ExternalLink,
  Paperclip,
  ArrowRight,
  Tag,
  HelpCircle,
  Info,
  TrendingUp
} from 'lucide-react';
import LeadToOpportunityModal from '../../apps/Pipeline/LeadToOpportunityModal';
import AttachmentServiceV2 from '../../components/Attachments/AttachmentServiceV2';
import TagService from '../../components/Tags/TagService';
import { formatCurrency } from '../../utils/currency';
import { api } from '../../utils/api';
import entityRelationService from '../../services/entityRelationService';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import CustomAttributesDisplay from '../../components/CustomAttributes/CustomAttributesDisplay';
import { extractCustomAttributeFields } from '../../components/CustomAttributes/utils';
import { useAuth } from '../../contexts/AuthContext';

const LeadDetail = ({ lead: propLead, onEdit, onClose, hiddenFields: propHiddenFields }) => {
  const { t } = useTranslation();
  const safeT = (key, fallback) => t(key, fallback);
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Load entity configuration with proper caching and loading states
  const { config: entityConfig, loading: configLoading, error: configError } = useEntityConfig('lead');
  const { user } = useAuth();
  const tenantCode =
    user?.selectedTenant?.code ||
    user?.selectedTenant?.tenant_code ||
    user?.tenantCode ||
    null;
  
  const [lead, setLead] = useState(propLead);
  const [loading, setLoading] = useState(!propLead);
  const [error, setError] = useState(null);
  const [customerCheck, setCustomerCheck] = useState({ loading: true, exists: false, duplicates: [] });
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [convertingToOpportunity, setConvertingToOpportunity] = useState(false);
  const [nameMapping, setNameMapping] = useState(null);
  const [showNameMapping, setShowNameMapping] = useState(false);
  const [showScoreTooltip, setShowScoreTooltip] = useState(false);
  const [customerType, setCustomerType] = useState('Person'); // 'Person' or 'Company'
  const [extractedData, setExtractedData] = useState(null);
  const [showDataExtraction, setShowDataExtraction] = useState(false);
  const [selectedData, setSelectedData] = useState({
    address: '',
    phone: '',
    email: '',
    website: '',
    location: '',
    industry: ''
  });
  
  // Dynamic list values
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [hiddenFields, setHiddenFields] = useState(propHiddenFields || []);
  const [related, setRelated] = useState({ customers: [], opportunities: [], activities: [] });
  const [entityRelations, setEntityRelations] = useState([]);
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const customAttributeFields = useMemo(() => {
    if (!entityConfig) return [];
    try {
      return extractCustomAttributeFields(entityConfig, {
        tenantCode,
        includeGlobal: true
      });
    } catch (err) {
      console.error('LeadDetail: Failed to extract custom attribute fields', err);
      return [];
    }
  }, [entityConfig, tenantCode]);

  const leadAttributes = useMemo(() => {
    const raw = lead?.Attributes ?? lead?.attributes;
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.warn('LeadDetail: Failed to parse attributes JSON', error);
        return {};
      }
    }
    return {};
  }, [lead?.Attributes, lead?.attributes, lead]);

  // Debug: Log related state changes
  useEffect(() => {
    console.log('🔄 [LeadDetail] related state changed:', {
      customersCount: related.customers.length,
      customers: related.customers,
      opportunitiesCount: related.opportunities.length,
      opportunities: related.opportunities,
      activitiesCount: related.activities.length,
      activities: related.activities,
      fullState: related
    });
  }, [related]);

  useEffect(() => {
    if (!propLead && id) {
      fetchLeadDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, propLead]);

  useEffect(() => {
    if (lead && lead.LeadID) {
      checkCustomerExists();
      loadRelatedEntities(lead.LeadID);
      loadEntityRelations(lead.LeadID);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.LeadID, lead?.ContactEmail, lead?.ContactPhone, lead?.ContactName]);

  // Update linked customer when related customers change
  useEffect(() => {
    if (related.customers && related.customers.length > 0) {
      // Use the first customer as the primary linked customer
      const primaryCustomer = related.customers[0];
      setLinkedCustomer(primaryCustomer);
    } else {
      setLinkedCustomer(null);
    }
  }, [related.customers]);

  // Load dynamic list values and hidden fields
  useEffect(() => {
    if (!entityConfig) return; // Wait for entity config to load
    
    const loadListValues = async () => {
      try {
        // The entityConfig returned from useEntityConfig hook is already the config object
        // Update Priority options if available in config
        if (entityConfig?.fields?.Priority?.ui?.options) {
          setPriorityOptions(entityConfig.fields.Priority.ui.options);
        }
        
        // Update Status options if available in config
        if (entityConfig?.fields?.Status?.ui?.options) {
          setStatusOptions(entityConfig.fields.Status.ui.options);
        }
        
        // Load hidden fields if not provided via props
        // Check for undefined/null/empty array
        if (propHiddenFields === undefined || propHiddenFields === null || (Array.isArray(propHiddenFields) && propHiddenFields.length === 0)) {
          // Extract hidden fields from config
          // The entityConfig returned from useEntityConfig hook already has hiddenFields at root level
          const configHiddenFields = entityConfig.hiddenFields || [];
          
          if (Array.isArray(configHiddenFields)) {
            setHiddenFields(configHiddenFields);
            console.log('✅ LeadDetail: Loaded hidden fields from config:', configHiddenFields);
          } else {
            console.warn('⚠️ LeadDetail: hiddenFields is not an array:', configHiddenFields);
            setHiddenFields([]);
          }
        }
      } catch (error) {
        console.error('Failed to load list values for detail view:', error);
        // Keep using empty arrays if loading fails
      }
    };
    
    loadListValues();
  }, [entityConfig, propHiddenFields]);

  const fetchLeadDetails = async (leadId = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const targetId = leadId || id || lead?.LeadID;
      
      if (!targetId) {
        console.error('No lead ID available for fetching details');
        return;
      }
      
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/leads/${targetId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch lead details (${response.status})`);
      }

      const data = await response.json();
      setLead(data.lead || data);
    } catch (err) {
      console.error('Error fetching lead details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedEntities = async (leadId) => {
    try {
      console.log('🔍 [LeadDetail] ===== START Loading entity relations for Lead:', leadId, '=====');
      
      // Customers related to this Lead (Lead → Customer)
      console.log('🔍 [LeadDetail] Calling getRelatedEntitiesByType for Customers...');
      const customersRelResp = await entityRelationService.getRelatedEntitiesByType('Lead', leadId, 'Customer', { timeoutMs: 12000 });
      console.log('🔍 [LeadDetail] Customer relations response received:', {
        responseType: typeof customersRelResp,
        responseKeys: customersRelResp ? Object.keys(customersRelResp) : 'null',
        hasRelations: !!customersRelResp?.relations,
        relationsCount: customersRelResp?.relations?.length || 0,
        fullResponse: customersRelResp
      });
      
      // Handle response structure - could be { relations: [...] } or just array
      const customerRelations = customersRelResp?.relations || (Array.isArray(customersRelResp) ? customersRelResp : []);
      const customerIds = customerRelations.map(r => r.TargetEntityID || r.targetEntityID).filter(Boolean);
      console.log('🔍 [LeadDetail] Customer IDs extracted:', customerIds);

      const customers = await Promise.all(
        customerIds.map(async (cid) => {
          try {
            const detail = await api.get(`/customers/${cid}`);
            return detail?.customer || detail || { CustomerID: cid };
          } catch (e) {
            console.warn('⚠️ [LeadDetail] Failed to fetch customer detail:', cid, e);
            return { CustomerID: cid, _fetchError: true };
          }
        })
      );

      // Opportunities related to this Lead (Lead → Opportunity)
      const oppRelResp = await entityRelationService.getRelatedEntitiesByType('Lead', leadId, 'Opportunity', { timeoutMs: 12000 });
      console.log('🔍 [LeadDetail] Opportunity relations response:', oppRelResp);
      
      const opportunityRelations = oppRelResp?.relations || (Array.isArray(oppRelResp) ? oppRelResp : []);
      const oppIds = opportunityRelations.map(r => r.TargetEntityID || r.targetEntityID).filter(Boolean);
      console.log('🔍 [LeadDetail] Opportunity IDs extracted:', oppIds);

      const opportunities = await Promise.all(
        oppIds.map(async (oid) => {
          try {
            const detail = await api.get(`/opportunities/${oid}`);
            return detail?.opportunity || detail || { OpportunityID: oid };
          } catch (e) {
            console.warn('⚠️ [LeadDetail] Failed to fetch opportunity detail:', oid, e);
            return { OpportunityID: oid, _fetchError: true };
          }
        })
      );

      // Activities related to this Lead (Lead → Activity)
      const actRelResp = await entityRelationService.getRelatedEntitiesByType('Lead', leadId, 'Activity', { timeoutMs: 12000 });
      console.log('🔍 [LeadDetail] Activity relations response:', actRelResp);
      
      // Extract activity IDs from relations regardless of structure/casing
      const activityRelations = actRelResp?.relations || (Array.isArray(actRelResp) ? actRelResp : []);
      const activityIds = activityRelations.map(r => r.TargetEntityID || r.targetEntityID || r.ActivityID || r.activityId).filter(Boolean);
      console.log('🔍 [LeadDetail] Activity IDs extracted:', activityIds);
      
      // Fetch activity details for consistent rendering
      const activities = await Promise.all(
        activityIds.map(async (aid) => {
          try {
            const detail = await api.get(`/activities/${aid}`);
            return detail?.activity || detail || { ActivityID: aid };
          } catch (e) {
            console.warn('⚠️ [LeadDetail] Failed to fetch activity detail:', aid, e);
            return { ActivityID: aid, _fetchError: true };
          }
        })
      );
      console.log('🔍 [LeadDetail] Activities fetched:', activities);

      console.log('✅ [LeadDetail] Final related entities before setState:', { 
        customersCount: customers.length,
        customers: customers,
        opportunitiesCount: opportunities.length,
        opportunities: opportunities,
        activitiesCount: activities.length,
        activities: activities
      });

      setRelated({ customers, opportunities, activities });
      
      console.log('✅ [LeadDetail] setRelated called, state should update');
      
      // Verify state was set correctly (will show in next render)
      setTimeout(() => {
        console.log('🔍 [LeadDetail] State verification (after setState):', {
          relatedCustomersCount: related.customers.length,
          relatedCustomers: related.customers,
          relatedOpportunitiesCount: related.opportunities.length,
          relatedActivitiesCount: related.activities.length
        });
      }, 100);
      
      console.log('🔍 [LeadDetail] ===== END Loading entity relations =====');
    } catch (error) {
      console.error('❌ [LeadDetail] Failed to load related entities for lead', leadId, error);
      setRelated({ customers: [], opportunities: [], activities: [] });
    }
  };

  // Load unified entity relations via entityRelationService
  const loadEntityRelations = async (leadId) => {
    try {
      console.log('🔍 [LeadDetail] loadEntityRelations - fetching unified relations for Lead', leadId);
      const relationsResp = await entityRelationService.getRelatedEntities('Lead', leadId, { direction: 'both', timeoutMs: 12000 });
      console.log('🔍 [LeadDetail] Unified relations raw response:', relationsResp, 'isArray:', Array.isArray(relationsResp));
      const relations = Array.isArray(relationsResp) ? relationsResp : (relationsResp?.relations || []);
      console.log('🔍 [LeadDetail] Unified relations count:', Array.isArray(relations) ? relations.length : 'N/A');
      const truncate = (text, max = 80) => {
        if (!text) return '';
        const trimmed = String(text).trim();
        return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
      };

      const mapped = relations.map(rel => {
        const srcTypeRaw = rel.SourceEntityType || rel.sourceEntityType;
        const tgtTypeRaw = rel.TargetEntityType || rel.targetEntityType;
        const srcId = rel.SourceEntityID || rel.sourceEntityID;
        const tgtId = rel.TargetEntityID || rel.targetEntityID;
        const isLeadSource = (srcTypeRaw || '').toString().toLowerCase() === 'lead';
        const entityTypeRaw = isLeadSource ? tgtTypeRaw : srcTypeRaw;
        const entityTypeLower = (entityTypeRaw || '').toString().toLowerCase();
        const entityType = entityTypeLower === 'customer' ? 'Customer'
          : entityTypeLower === 'lead' ? 'Lead'
          : entityTypeLower === 'opportunity' ? 'Opportunity'
          : entityTypeLower === 'activity' ? 'Activity'
          : entityTypeLower === 'salenote' || entityTypeLower === 'salesnote' ? 'SaleNote'
          : (entityTypeRaw || '');
        const entityId = isLeadSource ? tgtId : srcId;

        // Try to derive a display name from relation fields if present
        let entityName = '';
        switch (entityType) {
          case 'Customer':
            entityName = rel.CustomerName || `${rel.FirstName || ''} ${rel.LastName || ''}`.trim() || rel.CompanyName || `Customer ${entityId}`;
            // Store CustomerType if available in relation data
            if (rel.CustomerType) {
              return {
                EntityType: entityType,
                EntityID: entityId,
                EntityName: entityName,
                RelationID: rel.RelationID || rel.relationId,
                RelationType: rel.RelationType || rel.relationType,
                CustomerType: rel.CustomerType
              };
            }
            break;
          case 'Lead':
            entityName = rel.Title || rel.ContactName || rel.CompanyName || `Lead ${entityId}`;
            break;
          case 'Opportunity':
            entityName = rel.OpportunityName || rel.Title || `Opportunity ${entityId}`;
            break;
          case 'Activity':
            entityName = rel.Subject || `${rel.ActivityType || 'Activity'} ${entityId}`;
            break;
          case 'SaleNote':
            entityName = truncate((rel.NoteText ? String(rel.NoteText).split('\n')[0] : '')) || `Sale Note ${entityId}`;
            break;
          default:
            entityName = `${entityType} ${entityId}`;
        }

        return {
          EntityType: entityType,
          EntityID: entityId,
          EntityName: entityName,
          RelationID: rel.RelationID || rel.relationId,
          RelationType: rel.RelationType || rel.relationType,
          // Store CustomerType if available in relation data
          CustomerType: entityType === 'Customer' ? (rel.CustomerType || null) : undefined
        };
      });

      // Enrich all relations - especially Customer entities to get CustomerType
      const enriched = await Promise.all(mapped.map(async (m) => {
        try {
          // For Customer entities, always enrich to get CustomerType if not already available
          if (m.EntityType === 'Customer' && !m.CustomerType) {
            try {
              const detail = await api.get(`/customers/${m.EntityID}`);
              const cust = detail?.customer || detail || {};
              const customerType = cust.CustomerType || 'Company';
              const name = customerType === 'Person'
                ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                : cust.CompanyName || cust.CustomerName;
              return { ...m, EntityName: name || m.EntityName, CustomerType: customerType };
            } catch (e) {
              console.warn('⚠️ [LeadDetail] Failed to enrich Customer relation:', e);
              return { ...m, CustomerType: 'Company' };
            }
          }
          
          // Only fetch if we don't have a good name
          const hasDefaultName = !m.EntityName || m.EntityName === `${m.EntityType} ${m.EntityID}`;
          if (!hasDefaultName) return m;
          
          let detail;
          if (m.EntityType === 'Customer') {
            detail = await api.get(`/customers/${m.EntityID}`);
            const cust = detail?.customer || detail || {};
            const customerType = cust.CustomerType || m.CustomerType || 'Company';
            const name = customerType === 'Person'
              ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
              : cust.CompanyName || cust.CustomerName;
            return { ...m, EntityName: name || m.EntityName, CustomerType: customerType };
          } else if (m.EntityType === 'Lead') {
              detail = await api.get(`/leads/${m.EntityID}`);
              const ld = detail?.lead || detail || {};
              const name = ld.Title || ld.ContactName || ld.CompanyName;
              return { ...m, EntityName: name || m.EntityName };
            } else if (m.EntityType === 'Opportunity') {
              detail = await api.get(`/opportunities/${m.EntityID}`);
              const opp = detail?.opportunity || detail || {};
              const name = opp.OpportunityName || opp.CustomerName;
              return { ...m, EntityName: name || m.EntityName };
            } else if (m.EntityType === 'Activity') {
              detail = await api.get(`/activities/${m.EntityID}`);
              const act = detail?.activity || detail || {};
              const name = act.Subject || `${act.ActivityType || 'Activity'} ${m.EntityID}`;
              return { ...m, EntityName: name || m.EntityName };
            } else if (m.EntityType === 'SaleNote') {
              // Optional: best-effort; backend route may vary
              try {
                detail = await api.get(`/sales-notes/${m.EntityID}`);
                const sn = detail?.saleNote || detail || {};
                const name = truncate(sn.NoteText ? String(sn.NoteText).split('\n')[0] : (sn.Title || ''));
                return { ...m, EntityName: name || m.EntityName };
              } catch {
                return m;
              }
            }
        } catch (e) {
          console.warn('⚠️ [LeadDetail] Failed to enrich relation name for', m, e);
          return m;
        }
        return m;
      }));
      setEntityRelations(enriched);

      // Fallback: if unified call returned no relations, try per-type queries
      if (!mapped || mapped.length === 0) {
        console.warn('⚠️ [LeadDetail] No unified relations found, trying per-type fallbacks...');
        const types = ['Customer', 'Opportunity', 'Activity', 'SaleNote'];
        const perType = await Promise.all(types.map(async (t) => {
          try {
            console.log(`🔍 [LeadDetail] Fetching relations by type: ${t}`);
            const resp = await entityRelationService.getRelatedEntitiesByType('Lead', leadId, t, { timeoutMs: 10000 });
            console.log(`🔍 [LeadDetail] ${t} relations raw response:`, resp, 'keys:', resp ? Object.keys(resp) : 'null');
            const rels = resp?.relations || (Array.isArray(resp) ? resp : []);
            console.log(`🔍 [LeadDetail] ${t} relations count:`, Array.isArray(rels) ? rels.length : 'N/A');
            return rels.map(r => {
              const id = r.TargetEntityID || r.targetEntityID || r.EntityID || r.entityId;
              return {
                EntityType: t,
                EntityID: id,
                EntityName: r.EntityName || r.Title || r.Subject || r.CustomerName || `${t} ${id}`,
                RelationID: r.RelationID || r.relationId,
                RelationType: r.RelationType || r.relationType
              };
            });
          } catch {
            console.warn(`⚠️ [LeadDetail] Failed to fetch relations for type ${t}`);
            return [];
          }
        }));
        const flat = perType.flat().filter(x => x && x.EntityID);
        console.log('🔍 [LeadDetail] Fallback aggregated relations count:', flat.length);
        if (flat.length > 0) setEntityRelations(flat);
      }
    } catch (e) {
      console.warn('[LeadDetail] Failed to load entity relations:', e);
      setEntityRelations([]);
    }
  };

  // Helpers for entity relations UI (icons, paths, and colors)
  const getEntityColors = (entityType, customerType = null) => {
    // For Customer entities, use different colors based on CustomerType
    if (entityType === 'Customer' && customerType) {
      if (customerType === 'Person') {
        // Customer (Person) = blue
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          hoverBg: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
          icon: 'text-blue-600 dark:text-blue-400',
          badge: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
        };
      } else {
        // Customer (Company) = green
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      }
    }
    
    switch (entityType) {
      case 'Customer':
        // Default Customer (fallback) = green (Company)
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      case 'Lead':
        // Lead = red
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          hoverBg: 'hover:bg-red-100 dark:hover:bg-red-900/30',
          icon: 'text-red-600 dark:text-red-400',
          badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
        };
      case 'Opportunity':
        // Opportunity = violet
        return {
          bg: 'bg-violet-50 dark:bg-violet-900/20',
          border: 'border-violet-200 dark:border-violet-800',
          hoverBg: 'hover:bg-violet-100 dark:hover:bg-violet-900/30',
          icon: 'text-violet-600 dark:text-violet-400',
          badge: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200'
        };
      case 'Activity':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          hoverBg: 'hover:bg-orange-100 dark:hover:bg-orange-900/30',
          icon: 'text-orange-600 dark:text-orange-400',
          badge: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
        };
      case 'SaleNote':
        // SaleNote = yellow
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          hoverBg: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
          icon: 'text-yellow-600 dark:text-yellow-400',
          badge: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-700',
          border: 'border-gray-200 dark:border-gray-700',
          hoverBg: 'hover:bg-gray-100 dark:hover:bg-gray-600',
          icon: 'text-gray-600 dark:text-gray-400',
          badge: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
        };
    }
  };

  const getEntityIcon = (entityType) => {
    const colors = getEntityColors(entityType);
    const iconClass = `h-4 w-4 ${colors.icon}`;
    
    switch (entityType) {
      case 'Customer': return <Building className={iconClass} />;
      case 'Lead': return <User className={iconClass} />;
      case 'Opportunity': return <Target className={iconClass} />;
      case 'Activity': return <Calendar className={iconClass} />;
      case 'SaleNote': return <FileText className={iconClass} />;
      default: return <FileText className={iconClass} />;
    }
  };

  const getEntityPath = (entityType, entityId) => {
    switch (entityType) {
      case 'Customer': return `/customers/${entityId}`;
      case 'Lead': return `/leads/${entityId}`;
      case 'Opportunity': return `/opportunities/${entityId}`;
      case 'Activity': return `/activities/${entityId}`;
      case 'SaleNote': return `/notes?noteId=${entityId}`;
      default: return '#';
    }
  };

  const checkCustomerExists = async () => {
    try {
      setCustomerCheck({ loading: true, exists: false, duplicates: [] });
      
      if (!lead || !lead.LeadID) {
        setCustomerCheck({ loading: false, exists: false, duplicates: [] });
        return;
      }
      
      const duplicates = [];
      
      // Check by email
      if (lead.ContactEmail) {
        const emailResponse = await fetch(`/api/customers/search/${encodeURIComponent(lead.ContactEmail)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (emailResponse.ok) {
          const emailData = await emailResponse.json();
          if (emailData.results && emailData.results.length > 0) {
            emailData.results.forEach(customer => {
              duplicates.push({
                ...customer,
                matchType: 'email',
                matchField: lead.ContactEmail
              });
            });
          }
        }
      }
      
      // Check by phone (if no email match found)
      if (duplicates.length === 0 && lead.ContactPhone) {
        const phoneResponse = await fetch(`/api/customers/search/${encodeURIComponent(lead.ContactPhone)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (phoneResponse.ok) {
          const phoneData = await phoneResponse.json();
          if (phoneData.results && phoneData.results.length > 0) {
            phoneData.results.forEach(customer => {
              if (!duplicates.find(d => d.CustomerID === customer.CustomerID)) {
                duplicates.push({
                  ...customer,
                  matchType: 'phone',
                  matchField: lead.ContactPhone
                });
              }
            });
          }
        }
      }
      
      // Check by name (if no email or phone match)
      if (duplicates.length === 0 && lead.ContactName) {
        const nameResponse = await fetch(`/api/customers/search/${encodeURIComponent(lead.ContactName)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (nameResponse.ok) {
          const nameData = await nameResponse.json();
          if (nameData.results && nameData.results.length > 0) {
            nameData.results.forEach(customer => {
              if (!duplicates.find(d => d.CustomerID === customer.CustomerID)) {
                duplicates.push({
                  ...customer,
                  matchType: 'name',
                  matchField: lead.ContactName
                });
              }
            });
          }
        }
      }
      
      setCustomerCheck({
        loading: false,
        exists: duplicates.length > 0,
        duplicates: duplicates
      });
    } catch (err) {
      console.error('Error checking customer existence:', err);
      setCustomerCheck({ loading: false, exists: false, duplicates: [] });
    }
  };

  // Smart data extraction function
  const extractDataFromContent = (description, notes) => {
    const content = `${description || ''} ${notes || ''}`.toLowerCase();
    const extracted = {
      addresses: [],
      phones: [],
      emails: [],
      websites: [],
      locations: [],
      industries: [],
      jobTitles: [],
      departments: []
    };

    // Extract addresses (Thai addresses)
    const addressPatterns = [
      /ที่อยู่[:\s]*([^,]+(?:,\s*[^,]+)*)/gi,
      /address[:\s]*([^,]+(?:,\s*[^,]+)*)/gi,
      /(\d+\s*[^\s]+\s*(?:ซอย|ถนน|แขวง|เขต|จังหวัด)[^,]+)/gi,
      /(กรุงเทพ[^,]+|เชียงใหม่[^,]+|ขอนแก่น[^,]+|หาดใหญ่[^,]+|พัทยา[^,]+)/gi
    ];
    
    addressPatterns.forEach(pattern => {
      const matches = content.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const address = match.replace(/^(ที่อยู่|address)[:\s]*/i, '').trim();
          if (address.length > 10 && !extracted.addresses.includes(address)) {
            extracted.addresses.push(address);
          }
        });
      }
    });

    // Extract phone numbers
    const phonePatterns = [
      /(\d{3}[-.\s]?\d{3}[-.\s]?\d{4})/g,
      /(\d{2}[-.\s]?\d{3}[-.\s]?\d{4})/g,
      /(\+66[-.\s]?\d{2}[-.\s]?\d{3}[-.\s]?\d{4})/g,
      /(0\d{2}[-.\s]?\d{3}[-.\s]?\d{4})/g
    ];
    
    phonePatterns.forEach(pattern => {
      const matches = content.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const phone = match.replace(/[-.\s]/g, '');
          if (phone.length >= 8 && !extracted.phones.includes(phone)) {
            extracted.phones.push(phone);
          }
        });
      }
    });

    // Extract emails
    const emailPattern = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const emailMatches = content.match(emailPattern);
    if (emailMatches) {
      emailMatches.forEach(email => {
        if (!extracted.emails.includes(email)) {
          extracted.emails.push(email);
        }
      });
    }

    // Extract websites
    const websitePattern = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const websiteMatches = content.match(websitePattern);
    if (websiteMatches) {
      websiteMatches.forEach(website => {
        if (!extracted.websites.includes(website) && !website.includes('@')) {
          extracted.websites.push(website);
        }
      });
    }

    // Extract locations
    const locationPatterns = [
      /(กรุงเทพ[^,]*|เชียงใหม่[^,]*|ขอนแก่น[^,]*|หาดใหญ่[^,]*|พัทยา[^,]*|ภูเก็ต[^,]*|เชียงราย[^,]*|นครราชสีมา[^,]*)/gi,
      /(bangkok[^,]*|chiang mai[^,]*|phuket[^,]*|pattaya[^,]*)/gi
    ];
    
    locationPatterns.forEach(pattern => {
      const matches = content.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const location = match.trim();
          if (location.length > 2 && !extracted.locations.includes(location)) {
            extracted.locations.push(location);
          }
        });
      }
    });

    // Extract industries
    const industryKeywords = [
      'เทคโนโลยี', 'ซอฟต์แวร์', 'ไอที', 'การเงิน', 'ธนาคาร', 'ประกัน', 'อสังหาริมทรัพย์',
      'ก่อสร้าง', 'อาหาร', 'เครื่องดื่ม', 'แฟชั่น', 'สุขภาพ', 'การศึกษา', 'การท่องเที่ยว',
      'โลจิสติกส์', 'การขนส่ง', 'พลังงาน', 'สิ่งแวดล้อม', 'การเกษตร', 'อุตสาหกรรม',
      'technology', 'software', 'finance', 'banking', 'real estate', 'construction',
      'food', 'fashion', 'healthcare', 'education', 'tourism', 'logistics', 'energy'
    ];
    
    industryKeywords.forEach(keyword => {
      if (content.includes(keyword)) {
        if (!extracted.industries.includes(keyword)) {
          extracted.industries.push(keyword);
        }
      }
    });


    // Extract job titles
    const jobTitleKeywords = [
      'ceo', 'cto', 'cfo', 'coo', 'manager', 'director', 'head', 'lead', 'senior', 'junior',
      'กรรมการผู้จัดการ', 'ผู้จัดการ', 'ผู้อำนวยการ', 'หัวหน้า', 'ผู้ช่วย', 'ผู้เชี่ยวชาญ'
    ];
    
    jobTitleKeywords.forEach(keyword => {
      if (content.includes(keyword)) {
        if (!extracted.jobTitles.includes(keyword)) {
          extracted.jobTitles.push(keyword);
        }
      }
    });

    return extracted;
  };

  // Validate extracted data
  const validateExtractedData = (data) => {
    const validation = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Validate emails
    data.emails.forEach(email => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        validation.errors.push(`Invalid email format: ${email}`);
        validation.isValid = false;
      }
    });

    // Validate phones
    data.phones.forEach(phone => {
      const phoneRegex = /^(\+66|0)?[0-9]{8,10}$/;
      if (!phoneRegex.test(phone)) {
        validation.warnings.push(`Phone number format may be incorrect: ${phone}`);
      }
    });

    // Validate websites
    data.websites.forEach(website => {
      if (!website.startsWith('http') && !website.includes('.')) {
        validation.warnings.push(`Website format may be incorrect: ${website}`);
      }
    });

    return validation;
  };

  const handleCreateNewCustomer = async () => {
    console.log('🔍 handleCreateNewCustomer called, addingCustomer:', addingCustomer);
    
    // Extract data from description and notes
    const extracted = extractDataFromContent(lead.Description, lead.Notes);
    const validation = validateExtractedData(extracted);
    
    setExtractedData({ ...extracted, validation });
    
    // Set default selected data (first item from each category)
    setSelectedData({
      address: extracted.addresses[0] || '',
      phone: extracted.phones[0] || '',
      email: extracted.emails[0] || '',
      website: extracted.websites[0] || '',
      location: extracted.locations[0] || '',
      industry: extracted.industries[0] || ''
    });
    
    // Parse name
    const parsedName = parseThaiName(lead.ContactName);
    setNameMapping(parsedName);
    
    // Show data extraction preview first
    setShowDataExtraction(true);
    
    console.log('🔍 Data extraction completed, showDataExtraction:', true);
  };

  const handleConfirmCustomerCreation = async () => {
    try {
      setAddingCustomer(true);
      setShowDataExtraction(false);
      setShowNameMapping(false);
      
      // Use selected data to build customer data based on customer type
      const customerData = {
        customerType: customerType,
        leadId: lead.LeadID // Add lead ID to create relationship
      };
      
      if (customerType === 'Company') {
        // For company type, create company data
        customerData.company = {
          name: lead.CompanyName || 'Unknown Company',
          industry: selectedData.industry || lead.Industry || null,
          website: selectedData.website || lead.Website || null,
          location: selectedData.location || null,
          address: selectedData.address || null
        };
        
        // Add contact person for company if available
        if (lead.ContactName) {
          const parsedName = parseThaiName(lead.ContactName);
          customerData.person = {
            firstName: parsedName.firstName || 'Contact',
            lastName: parsedName.lastName || 'Person',
            email: lead.ContactEmail || selectedData.email || null,
            phone: lead.ContactPhone || selectedData.phone || null,
            title: parsedName.title || lead.JobTitle || lead.Title || null
          };
        }
      } else {
        // For person type, create person data
        const firstName = nameMapping.firstName || 'Unknown';
        const lastName = nameMapping.lastName || 'Contact';
        
        customerData.person = {
          firstName: firstName,
          lastName: lastName,
          email: lead.ContactEmail || selectedData.email || null,
          phone: lead.ContactPhone || selectedData.phone || null,
          title: nameMapping.title || lead.JobTitle || lead.Title || null
        };
        
        // Add address if selected
        if (selectedData.address) {
          customerData.person.address = selectedData.address;
        }
        
        // If company name exists, add company data
        if (lead.CompanyName) {
          customerData.company = {
            name: lead.CompanyName,
            industry: selectedData.industry || lead.Industry || null,
            website: selectedData.website || lead.Website || null,
            location: selectedData.location || null,
            address: selectedData.address || null
          };
        }
      }
      
      console.log('🔍 Smart customer data:', customerData);
      console.log('🔍 Customer type:', customerType);
      console.log('🔍 Selected data:', selectedData);
      console.log('🔍 Lead data:', lead);

      const response = await fetch('/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(customerData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Customer creation failed:', errorData);
        throw new Error(errorData.error || errorData.message || 'Failed to create customer');
      }
      
      const result = await response.json();
      
      // Update lead conversion status and create entity relation
      if (result.customerId) {
        try {
          // Update lead conversion status
          const selectedRole = localStorage.getItem('selectedRole');
          const updateResponse = await fetch(`/api/leads/${lead.LeadID}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
            },
            body: JSON.stringify({
              ConvertedToCustomer: true,
              ConversionDate: new Date().toISOString()
            })
          });
          
          if (updateResponse.ok) {
            setLead(prev => ({
              ...prev,
              ConvertedToCustomer: true,
              ConversionDate: new Date().toISOString()
            }));
          }

          // Create entity relation between Lead and Customer
          try {
            // Check if relation already exists (backend might have created it)
            const existingRelations = await entityRelationService.getRelatedEntitiesByType('Lead', lead.LeadID, 'Customer');
            const relationExists = existingRelations.relations?.some(
              r => r.TargetEntityID === result.customerId && r.RelationType === 'converted_from'
            );
            
            if (!relationExists) {
              await entityRelationService.createLeadCustomerConversion(
                lead.LeadID,
                result.customerId,
                {
                  customerType: customerType,
                  conversionMethod: 'manual'
                }
              );
              console.log('✅ Entity relation created between Lead and Customer');
            } else {
              console.log('ℹ️ Entity relation already exists between Lead and Customer');
            }
          } catch (relationError) {
            console.warn('⚠️ Failed to create entity relation:', relationError);
            // Don't fail the whole operation if relation creation fails
          }
        } catch (updateError) {
          console.warn('Error updating lead conversion status:', updateError);
        }
      }
      
      setShowAddCustomerModal(false);
      await checkCustomerExists();
      
      // Reload related entities to show the newly created customer
      await loadRelatedEntities(lead.LeadID);
      
      alert(safeT('leads.customerCreatedSuccess', 'Customer created successfully with smart data extraction!'));
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(err.message || 'Failed to create customer. Please try again.');
      alert(err.message || safeT('leads.customerCreatedError', 'Failed to create customer. Please try again.'));
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleMergeWithExisting = async (customerId) => {
    try {
      setAddingCustomer(true);
      
      // Fetch customer details to sync lead information
      let customerData = {};
      try {
        const customerDetail = await api.get(`/customers/${customerId}`);
        const customer = customerDetail?.customer || customerDetail || {};
        customerData = customer;
        console.log('🔍 LeadDetail: Customer data for merge:', customer);
      } catch (e) {
        console.warn('⚠️ LeadDetail: Failed to fetch customer details, proceeding with merge anyway:', e);
      }
      
      // Build update payload with customer information
      const updatePayload = {
        ConvertedToCustomer: true,
        ConversionDate: new Date().toISOString(),
        Notes: (lead.Notes || '') + `\n\nMerged with existing customer ID: ${customerId}`
      };
      
      // Sync contact and company information from customer
      if (customerData.CustomerType === 'Person') {
        // Person customer - use person fields
        if (customerData.FirstName || customerData.LastName || customerData.CustomerName) {
          updatePayload.ContactName = `${customerData.FirstName || ''} ${customerData.LastName || ''}`.trim() || customerData.CustomerName || lead.ContactName;
        }
        if (customerData.JobTitle || customerData.Position) {
          updatePayload.JobTitle = customerData.JobTitle || customerData.Position || lead.JobTitle;
        }
        if (customerData.PersonEmail || customerData.Email) {
          updatePayload.ContactEmail = customerData.PersonEmail || customerData.Email || lead.ContactEmail;
        }
        if (customerData.PersonPhone || customerData.Phone) {
          updatePayload.ContactPhone = customerData.PersonPhone || customerData.Phone || lead.ContactPhone;
        }
        // Company information for person customer
        if (customerData.PersonCompanyName || customerData.CompanyName) {
          updatePayload.CompanyName = customerData.PersonCompanyName || customerData.CompanyName || lead.CompanyName;
        }
        if (customerData.PersonCompanyIndustry || customerData.Industry) {
          updatePayload.Industry = customerData.PersonCompanyIndustry || customerData.Industry || lead.Industry;
        }
        if (customerData.PersonCompanyWebsite || customerData.Website) {
          updatePayload.Website = customerData.PersonCompanyWebsite || customerData.Website || lead.Website;
        }
      } else {
        // Company customer - use company fields
        if (customerData.CompanyName || customerData.CustomerName) {
          updatePayload.CompanyName = customerData.CompanyName || customerData.CustomerName || lead.CompanyName;
        }
        if (customerData.Industry) {
          updatePayload.Industry = customerData.Industry || lead.Industry;
        }
        if (customerData.Website) {
          updatePayload.Website = customerData.Website || lead.Website;
        }
        // Contact person for company
        if (customerData.FirstName || customerData.LastName || customerData.CustomerName) {
          updatePayload.ContactName = `${customerData.FirstName || ''} ${customerData.LastName || ''}`.trim() || customerData.CustomerName || lead.ContactName;
        }
        if (customerData.JobTitle || customerData.Position) {
          updatePayload.JobTitle = customerData.JobTitle || customerData.Position || lead.JobTitle;
        }
        if (customerData.PersonEmail || customerData.Email) {
          updatePayload.ContactEmail = customerData.PersonEmail || customerData.Email || lead.ContactEmail;
        }
        if (customerData.PersonPhone || customerData.Phone) {
          updatePayload.ContactPhone = customerData.PersonPhone || customerData.Phone || lead.ContactPhone;
        }
      }
      
      console.log('🔍 LeadDetail: Updating lead with customer data:', updatePayload);
      
      // Update lead with customer information
      const selectedRole = localStorage.getItem('selectedRole');
      const updateResponse = await fetch(`/api/leads/${lead.LeadID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: JSON.stringify(updatePayload)
      });
      
      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}));
        console.error('Failed to update lead:', errorData);
        throw new Error(errorData.message || 'Failed to update lead');
      }
      
      const updatedLead = await updateResponse.json();
      const leadData = updatedLead.lead || updatedLead;
      
      // Update local state immediately with all synced data
      setLead(prev => ({
        ...prev,
        ...updatePayload,
        ...leadData
      }));
      
      // Create entity relation between Lead and Customer
      try {
        // Check if relation already exists
        const existingRelations = await entityRelationService.getRelatedEntitiesByType('Lead', lead.LeadID, 'Customer');
        const relationExists = existingRelations.relations?.some(
          r => r.TargetEntityID === customerId && r.RelationType === 'converted_from'
        );
        
        if (!relationExists) {
          await entityRelationService.createLeadCustomerConversion(
            lead.LeadID,
            customerId,
            {
              conversionMethod: 'merge_with_existing'
            }
          );
          console.log('✅ Entity relation created between Lead and existing Customer');
        } else {
          console.log('ℹ️ Entity relation already exists between Lead and Customer');
        }
      } catch (relationError) {
        console.warn('⚠️ Failed to create entity relation:', relationError);
        // Don't fail the whole operation if relation creation fails
      }
      
      setShowAddCustomerModal(false);
      
      // Recheck customer status and reload related entities
      await checkCustomerExists();
      await loadRelatedEntities(lead.LeadID);
      
      alert(safeT('leads.customerMergedSuccess', 'Lead marked as converted to existing customer!'));
    } catch (err) {
      console.error('Error merging with customer:', err);
      alert(safeT('leads.customerMergedError', err.message || 'Failed to merge with customer. Please try again.'));
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else {
      navigate(`/leads/${id}/edit`);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/leads');
    }
  };

  // Handle lead to opportunity conversion
  const handleConversionSubmit = async (leadId, opportunityData) => {
    setConvertingToOpportunity(true);
    try {
      const data = await api.post(`/leads/${leadId}/convert-to-opportunity`, opportunityData);
      const createdOpportunityId = data.opportunityId;
      
      setShowConversionModal(false);
      
      // Update local lead state to reflect conversion
      setLead(prev => ({
        ...prev,
        ConvertedToOpportunity: true,
        ConversionDate: new Date().toISOString()
      }));
      
      // Show success message and optionally navigate to the new opportunity
      const viewOpportunity = window.confirm(
        safeT('leads.opportunityCreatedConfirm', 
          'Lead successfully converted to opportunity! Would you like to view the new opportunity?')
      );
      
      if (viewOpportunity && createdOpportunityId) {
        navigate(`/opportunities/${createdOpportunityId}`);
      } else {
        // Reload related entities to show the new opportunity
        await loadRelatedEntities(leadId);
        await loadEntityRelations(leadId);
      }
    } catch (err) {
      console.error('Error converting lead to opportunity:', err);
      alert(safeT('leads.opportunityConversionError', err.message || 'Failed to convert lead to opportunity. Please try again.'));
    } finally {
      setConvertingToOpportunity(false);
    }
  };

  if (loading || configLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {configLoading ? 'Loading configuration...' : 'Loading lead details...'}
        </p>
      </div>
    );
  }

  if (error || configError) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
          {configError ? 'Configuration Error' : 'Error'}
        </h3>
        <p className="text-red-600 dark:text-red-400">{configError || error}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-600">{t('leads.leadNotFound', 'Lead not found')}</p>
      </div>
    );
  }

  const getPriorityColor = (priority) => {
    // Find the priority option to get its color
    const priorityOption = priorityOptions.find(opt => opt.value === priority);
    if (priorityOption && priorityOption.color) {
      const colorMap = {
        'red': 'bg-red-100 text-red-800 border-red-200',
        'orange': 'bg-orange-100 text-orange-800 border-orange-200',
        'yellow': 'bg-yellow-100 text-yellow-800 border-yellow-200',
        'green': 'bg-green-100 text-green-800 border-green-200',
        'gray': 'bg-gray-100 text-gray-800 border-gray-200',
        'blue': 'bg-blue-100 text-blue-800 border-blue-200',
        'purple': 'bg-purple-100 text-purple-800 border-purple-200'
      };
      return colorMap[priorityOption.color] || 'bg-gray-100 text-gray-800 border-gray-200';
    }
    
    // Fallback to original logic for backward compatibility
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    // Find the status option to get its color
    const statusOption = statusOptions.find(opt => opt.value === status);
    if (statusOption && statusOption.color) {
      const colorMap = {
        'red': 'bg-red-100 text-red-800 border-red-200',
        'orange': 'bg-orange-100 text-orange-800 border-orange-200',
        'yellow': 'bg-yellow-100 text-yellow-800 border-yellow-200',
        'green': 'bg-green-100 text-green-800 border-green-200',
        'gray': 'bg-gray-100 text-gray-800 border-gray-200',
        'blue': 'bg-blue-100 text-blue-800 border-blue-200',
        'purple': 'bg-purple-100 text-purple-800 border-purple-200'
      };
      return colorMap[statusOption.color] || 'bg-blue-100 text-blue-800 border-blue-200';
    }
    
    // Fallback to default blue for status
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getQualificationColor = (level) => {
    switch (level) {
      case 'Hot': return 'bg-red-100 text-red-800 border-red-200';
      case 'Warm': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Cold': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Qualified': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Helper function to check if field is hidden
  const isFieldHidden = (fieldName) => {
    return hiddenFields.includes(fieldName);
  };

  // Helper function to check if any field in a group is visible
  const hasVisibleFieldsInGroup = (fieldsInGroup) => {
    return fieldsInGroup.some(fieldName => !isFieldHidden(fieldName));
  };

  // Removed unused helper renderFieldIfVisible to satisfy lint

  const getScoreReasons = (lead) => {
    const reasons = [];
    const score = lead.Score || 0;
    
    // Reason based on data completeness
    let completenessScore = 0;
    if (lead.ContactEmail) completenessScore += 25;
    if (lead.ContactPhone) completenessScore += 15;
    if (lead.LinkedIn || lead.Website) completenessScore += 20;
    if (lead.JobTitle) completenessScore += 15;
    if (lead.Description) completenessScore += 10;
    
    if (completenessScore >= 60) {
      reasons.push(`✅ ข้อมูลครบถ้วน (+${completenessScore}%)`);
    } else if (completenessScore >= 40) {
      reasons.push(`⚠️ ข้อมูลพอใช้ (+${completenessScore}%)`);
    } else {
      reasons.push(`❌ ข้อมูลไม่ครบ (+${completenessScore}%)`);
    }
    
    // Reason based on source
    if (lead.LeadSource) {
      if (lead.LeadSource.includes('DBD') || lead.LeadSource.includes('กรมพัฒนา')) {
        reasons.push('✅ แหล่งข้อมูลทางการ (+15%)');
      } else if (lead.LeadSource.includes('LinkedIn')) {
        reasons.push('✅ โปรไฟล์ LinkedIn (+10%)');
      } else if (lead.LeadSource.includes('.co.th') || lead.LeadSource.includes('Thai Business')) {
        reasons.push('✅ เว็บไซต์บริษัทไทย (+10%)');
      } else if (lead.LeadSource.includes('AI Generated')) {
        reasons.push('🤖 สร้างโดย AI (+5%)');
      }
    }
    
    // Reason based on contact info quality
    if (lead.ContactEmail && lead.ContactEmail.includes('@') && !lead.ContactEmail.includes('example')) {
      reasons.push('✅ อีเมลที่ตรวจสอบได้ (+10%)');
    }
    
    if (lead.ContactPhone) {
      reasons.push('✅ มีเบอร์โทรศัพท์ (+10%)');
    }
    
    // Reason based on company info
    if (lead.CompanyName && lead.CompanyName !== 'Unknown Company') {
      reasons.push('✅ ชื่อบริษัทชัดเจน (+5%)');
    }
    
    // Reason based on job title relevance
    if (lead.JobTitle && (
      lead.JobTitle.includes('Director') || 
      lead.JobTitle.includes('Manager') || 
      lead.JobTitle.includes('CEO') || 
      lead.JobTitle.includes('CTO') ||
      lead.JobTitle.includes('ผู้จัดการ') ||
      lead.JobTitle.includes('กรรมการ')
    )) {
      reasons.push('✅ ตำแหน่งระดับผู้บริหาร (+10%)');
    }
    
    // Reason based on conversion status
    if (lead.ConvertedToCustomer) {
      reasons.push('✅ แปลงเป็นลูกค้าแล้ว (+15%)');
    }
    
    if (lead.ConvertedToOpportunity) {
      reasons.push('✅ สร้างโอกาสทางการขายแล้ว (+10%)');
    }
    
    // Add qualification level
    const qualLevel = lead.QualificationLevel || 'Warm';
    if (qualLevel === 'Hot') {
      reasons.push('🔥 คุณภาพสูง (Hot Lead)');
    } else if (qualLevel === 'Warm') {
      reasons.push('🌟 คุณภาพดี (Warm Lead)');
    } else if (qualLevel === 'Cold') {
      reasons.push('❄️ คุณภาพปานกลาง (Cold Lead)');
    } else if (qualLevel === 'Qualified') {
      reasons.push('✅ คุณสมบัติครบถ้วน (Qualified Lead)');
    }
    
    return {
      score,
      reasons,
      summary: score >= 80 ? 'Lead คุณภาพสูง' : score >= 60 ? 'Lead คุณภาพดี' : score >= 40 ? 'Lead คุณภาพปานกลาง' : 'Lead ต้องติดตามเพิ่มเติม'
    };
  };

  // Helper function to format currency (now using utility)
  const formatCurrencyLocal = (amount, currency = 'THB') => {
    return formatCurrency(amount, currency);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatLeadName = (lead) => {
    return lead.ContactName || lead.CompanyName || `Lead #${lead.LeadID}`;
  };

  // Thai name parsing function
  const parseThaiName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') {
      return { title: '', firstName: '', lastName: '' };
    }

    // Thai titles
    const thaiTitles = ['นาย', 'นาง', 'นางสาว', 'ดร.', 'ศ.', 'รศ.', 'ผศ.', 'คุณ'];
    
    // Split by spaces and filter empty strings
    const nameParts = fullName.trim().split(/\s+/).filter(part => part.length > 0);
    
    console.log('🔍 Thai name parsing debug:', {
      fullName,
      nameParts,
      namePartsLength: nameParts.length,
      namePartsDetails: nameParts.map((part, index) => ({
        index,
        part,
        length: part.length,
        bytes: new TextEncoder().encode(part).length,
        charCodes: part.split('').map(c => c.charCodeAt(0))
      }))
    });
    
    if (nameParts.length === 0) {
      return { title: '', firstName: '', lastName: '' };
    }
    
    // Check if first part is a Thai title
    let title = '';
    let firstName = '';
    let lastName = '';      
    
    if (nameParts.length > 0 && thaiTitles.includes(nameParts[0])) {
      title = nameParts[0];
      if (nameParts.length >= 2) {
        firstName = nameParts[1];
        lastName = nameParts.slice(2).join(' ');
      }
    } else {
      // No title, treat first part as first name
      if (nameParts.length >= 1) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' ');
      }
    }
    
    console.log('🔍 Parsed result:', {
      title,
      firstName,
      lastName,
      titleLength: title.length,
      firstNameLength: firstName.length,
      lastNameLength: lastName.length,
      titleBytes: new TextEncoder().encode(title).length,
      firstNameBytes: new TextEncoder().encode(firstName).length,
      lastNameBytes: new TextEncoder().encode(lastName).length,
      titleCharCodes: title ? title.split('').map(c => c.charCodeAt(0)) : [],
      firstNameCharCodes: firstName ? firstName.split('').map(c => c.charCodeAt(0)) : [],
      lastNameCharCodes: lastName ? lastName.split('').map(c => c.charCodeAt(0)) : []
    });
    
    return { title, firstName, lastName };
  };

  return (
    <div className="max-w-6xl mx-auto space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
        <div className="flex items-start space-x-2 sm:space-x-4">
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-2 rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 touch-target"
          >
            <ArrowLeft className="h-4 w-4 dark:text-gray-300" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">
              {(
                (lead.Title ? lead.Title : '') +
                (lead.ContactName ? (lead.Title ? ' - ' : '') + lead.ContactName : '')
              ) || formatLeadName(lead)}
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('leads.leadDetails', 'Lead Information and Details')}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleEdit}
            className="flex-1 sm:flex-none bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm hover:bg-blue-700 touch-target no-tap-highlight"
          >
            <Edit2 className="h-4 w-4 flex-shrink-0" />
            <span>{t('common.edit')}</span>
          </button>
          
          {/* Convert to Opportunity Button */}
          {!lead.ConvertedToOpportunity && (
            <button
              onClick={() => setShowConversionModal(true)}
              disabled={convertingToOpportunity}
              className="flex-1 sm:flex-none bg-purple-600 text-white px-3 py-2 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed touch-target no-tap-highlight transition-colors"
              title={safeT('leads.convertToOpportunityTooltip', 'Convert this lead into a sales opportunity')}
            >
              <TrendingUp className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline">{safeT('leads.convertToOpportunity', 'Convert to Opportunity')}</span>
              <span className="sm:hidden">{safeT('leads.convertShort', 'Convert')}</span>
            </button>
          )}
          
          {/* Show text indicator if already converted */}
          {lead.ConvertedToOpportunity && (() => {
            // Find the related opportunity
            const relatedOpportunity = related.opportunities?.[0] || 
              entityRelations.find(r => r.EntityType === 'Opportunity');
            const opportunityId = relatedOpportunity?.OpportunityID || relatedOpportunity?.EntityID;
            const opportunityName = relatedOpportunity?.Title || relatedOpportunity?.OpportunityName || relatedOpportunity?.EntityName;
            
            return (
              <div className="flex items-center space-x-2 text-purple-600 dark:text-purple-400">
                <TrendingUp className="h-4 w-4 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium">
                  {safeT('leads.alreadyConvertedToOpportunity', 'แปลงเป็นโอกาสทางการขายแล้ว / Already converted to Opportunity')}
                </span>
                {opportunityId && (
                  <>
                    <span className="text-gray-400 dark:text-gray-500">|</span>
                    <button
                      onClick={() => navigate(`/opportunities/${opportunityId}`)}
                      className="inline-flex items-center space-x-1 text-xs sm:text-sm font-medium text-purple-700 dark:text-purple-300 hover:text-purple-900 dark:hover:text-purple-100 hover:underline transition-colors"
                      title={opportunityName || safeT('leads.viewOpportunity', 'View Opportunity')}
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span>{safeT('leads.viewOpportunity', 'ดูโอกาส / View Opportunity')}</span>
                    </button>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          {hasVisibleFieldsInGroup(['Title', 'LeadSource', 'Status', 'Priority']) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Target className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium dark:text-white">{t('leads.basicInfo', 'Basic Information')}</h3>
              </div>
              
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               {!isFieldHidden('Title') && (
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('leads.title')}
                   </label>
                   <p className="text-sm text-gray-900 dark:text-white">{lead.Title || '-'}</p>
                 </div>
               )}
               
               {!isFieldHidden('LeadSource') && (
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('leads.leadSource')}
                   </label>
                   <p className="text-sm text-gray-900 dark:text-white">{lead.LeadSource || '-'}</p>
                 </div>
               )}

               {!isFieldHidden('Status') && (
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('leads.status')}
                   </label>
                   <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(lead.Status)}`}>
                     {lead.Status}
                   </span>
                 </div>
               )}

               {!isFieldHidden('Priority') && (
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('leads.priority')}
                   </label>
                   <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(lead.Priority)}`}>
                     {lead.Priority}
                   </span>
                 </div>
               )}
               </div>
            </div>
          )}

          {/* Contact Information */}
          {hasVisibleFieldsInGroup(['ContactName', 'JobTitle', 'ContactEmail', 'ContactPhone']) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium dark:text-white">{t('leads.contactInfo', 'Contact Information')}</h3>
              </div>
              
              <div className="flex items-center space-x-2">
                {/* Show indicator when using customer information */}
                {linkedCustomer && (
                  <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-1.5">
                    <Building className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      {t('leads.usingCustomerInfo', 'Using Customer Information')}
                    </span>
                  </div>
                )}
                
                {/* Add to Customer Button - Hide when linkedCustomer exists */}
                {(() => {
                  // Show button if:
                  // 1. Not loading AND
                  // 2. No linked customer AND
                  // 3. Either not converted to customer OR (converted but primary customer doesn't exist)
                  const shouldShowButton = !customerCheck.loading && 
                    !linkedCustomer &&
                    (!lead.ConvertedToCustomer || (lead.ConvertedToCustomer && !customerCheck.exists));
                  
                  return shouldShowButton && (
                    <div className="relative group">
                      {!customerCheck.exists ? (
                        <button
                          onClick={() => {
                            console.log('🔍 Create new customer clicked, addingCustomer:', addingCustomer);
                            setShowAddCustomerModal(true);
                          }}
                          className={`text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm transition-all ${
                            lead.ConvertedToCustomer 
                              ? 'bg-orange-600 hover:bg-orange-700 shadow-md hover:shadow-lg' 
                              : 'bg-green-600 hover:bg-green-700 shadow-md hover:shadow-lg'
                          }`}
                          title={safeT('leads.createNewCustomerTooltip', 'Create a new customer record from this lead contact')}
                        >
                          <Users className="h-4 w-4" />
                          <span>
                            {lead.ConvertedToCustomer 
                              ? safeT('leads.recreateCustomer', 'Recreate Customer') 
                              : safeT('leads.createNewCustomerFromContact', 'Create new customer from this contact')
                            }
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            console.log('🔍 Merge to existing customer clicked, addingCustomer:', addingCustomer);
                            setShowAddCustomerModal(true);
                          }}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-yellow-700 text-sm shadow-md hover:shadow-lg transition-all"
                          title={safeT('leads.mergeToExistingTooltip', 'We found a customer with matching name/email. Click to review and merge or create new.')}
                        >
                          <Users className="h-4 w-4" />
                          <span>{safeT('leads.mergeToExistingCustomer', 'Merge this contact to existing customer')}</span>
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Information Banner - Show when contact is not a customer */}
            {!linkedCustomer && !customerCheck.loading && (
              <div className={`mb-4 p-3 rounded-lg flex items-start space-x-3 ${
                customerCheck.exists 
                  ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                  : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
              }`}>
                <Info className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                  customerCheck.exists 
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`} />
                <div className="flex-1">
                  {customerCheck.exists ? (
                    <>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        {safeT('leads.contactNotCustomerButDuplicate', 'พบข้อมูลลูกค้าที่มีชื่อ/อีเมลตรงกัน')}
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                        {safeT('leads.contactNotCustomerButDuplicateDesc', 'This contact has not been saved as a customer yet, but we found an existing customer with the same name or email. If this is the same person, click "Merge this contact to existing customer" to link them. Otherwise, you can create a new customer record.')}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                        {safeT('leads.contactNotCustomer', 'ข้อมูลผู้ติดต่อนี้ยังไม่ได้บันทึกว่าเป็นลูกค้า')}
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        {safeT('leads.contactNotCustomerDesc', 'This contact has not been recorded as a customer yet. Click "Create new customer from this contact" to save this lead as a customer record in your CRM.')}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               {!isFieldHidden('ContactName') && (
                 <div className="relative group">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center space-x-1">
                     <span>{t('leads.contactName')}</span>
                     <div className="relative">
                       <HelpCircle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
                       <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg">
                         {t('leads.contactNameTooltip', 'The full name of the contact person associated with this lead. This is the primary contact for communication.')}
                         <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                       </div>
                     </div>
                   </label>
                   <div className="text-sm text-gray-900 dark:text-white">
                     {linkedCustomer && linkedCustomer.CustomerType === 'Person' ? (
                       <div className="flex items-center space-x-2">
                         <button
                           onClick={() => navigate(`/customers/${linkedCustomer.CustomerID}`)}
                           className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline font-medium flex items-center space-x-1"
                         >
                           <span>{`${linkedCustomer.FirstName || ''} ${linkedCustomer.LastName || ''}`.trim() || linkedCustomer.CustomerName || '-'}</span>
                           <ExternalLink className="h-3 w-3" />
                         </button>
                         <span className="text-xs text-blue-600 dark:text-blue-400">(Customer)</span>
                       </div>
                     ) : (
                       <span>{lead.ContactName || '-'}</span>
                     )}
                   </div>
                 </div>
               )}
               
               {!isFieldHidden('JobTitle') && (
                 <div className="relative group">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center space-x-1">
                     <span>{t('leads.jobTitle')}</span>
                     <div className="relative">
                       <HelpCircle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
                       <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg">
                         {t('leads.jobTitleTooltip', 'The job title or position of the contact person. This helps identify their role and decision-making authority within the organization.')}
                         <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                       </div>
                     </div>
                   </label>
                   <p className="text-sm text-gray-900 dark:text-white">
                     {linkedCustomer && linkedCustomer.CustomerType === 'Person'
                       ? linkedCustomer.JobTitle || linkedCustomer.Position || lead.JobTitle || '-'
                       : lead.JobTitle || '-'}
                     {linkedCustomer && (linkedCustomer.JobTitle || linkedCustomer.Position) && (
                       <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(from Customer)</span>
                     )}
                   </p>
                 </div>
               )}

               {!isFieldHidden('ContactEmail') && (
                 <div className="relative group">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center space-x-1">
                     <span>{t('leads.email')}</span>
                     <div className="relative">
                       <HelpCircle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
                       <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg">
                         {t('leads.emailTooltip', 'The email address of the contact person. Click to send an email. This is used for communication and follow-up activities.')}
                         <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                       </div>
                     </div>
                   </label>
                   <div className="flex items-center space-x-2">
                     <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                     {(() => {
                       const email = linkedCustomer && linkedCustomer.CustomerType === 'Person'
                         ? (linkedCustomer.PersonEmail || linkedCustomer.Email)
                         : lead.ContactEmail;
                       
                       return email ? (
                         <>
                           <a 
                             href={`mailto:${email}`}
                             className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                             title={`${t('leads.sendEmailTo', 'Send email to')} ${email}`}
                           >
                             {email}
                           </a>
                           {linkedCustomer && (linkedCustomer.PersonEmail || linkedCustomer.Email) && (
                             <span className="text-xs text-blue-600 dark:text-blue-400">(Customer)</span>
                           )}
                         </>
                       ) : (
                         <p className="text-sm text-gray-900 dark:text-white">-</p>
                       );
                     })()}
                   </div>
                 </div>
               )}

               {!isFieldHidden('ContactPhone') && (
                 <div className="relative group">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center space-x-1">
                     <span>{t('leads.phone')}</span>
                     <div className="relative">
                       <HelpCircle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
                       <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg">
                         {t('leads.phoneTooltip', 'The phone number of the contact person. This is used for direct communication, calls, and SMS notifications.')}
                         <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                       </div>
                     </div>
                   </label>
                   <div className="flex items-center space-x-2">
                     <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                     {(() => {
                       const phone = linkedCustomer && linkedCustomer.CustomerType === 'Person'
                         ? (linkedCustomer.PersonPhone || linkedCustomer.Phone)
                         : lead.ContactPhone;
                       
                       return (
                         <>
                           <p className="text-sm text-gray-900 dark:text-white">{phone || '-'}</p>
                           {linkedCustomer && (linkedCustomer.PersonPhone || linkedCustomer.Phone) && (
                             <span className="text-xs text-blue-600 dark:text-blue-400">(Customer)</span>
                           )}
                         </>
                       );
                     })()}
                   </div>
                 </div>
               )}
             </div>
            </div>
          )}

          {/* Company Information */}
          {hasVisibleFieldsInGroup(['CompanyName', 'Industry', 'Website']) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium dark:text-white">{t('leads.companyInfo', 'Company Information')}</h3>
              </div>
              
              {/* Show indicator when using customer company information */}
              {linkedCustomer && (linkedCustomer.CustomerType === 'Company' || linkedCustomer.CompanyName || (linkedCustomer.CustomerType === 'Person' && linkedCustomer.CompanyName)) && (
                <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-1.5">
                  <Building className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                    Using Customer Company
                  </span>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               {!isFieldHidden('CompanyName') && (
                 <div className="relative group">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center space-x-1">
                     <span>{t('leads.companyName')}</span>
                     <div className="relative">
                       <HelpCircle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
                       <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg">
                         {t('leads.companyNameTooltip', 'The name of the company or organization associated with this lead. This helps identify the business entity.')}
                         <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                       </div>
                     </div>
                   </label>
                   <div className="text-sm text-gray-900 dark:text-white">
                     {linkedCustomer && (linkedCustomer.CompanyName || linkedCustomer.PersonCompanyName || (linkedCustomer.CustomerType === 'Company' && linkedCustomer.CustomerName)) ? (
                       <div className="flex items-center space-x-2">
                         <button
                           onClick={() => navigate(`/customers/${linkedCustomer.CustomerID}`)}
                           className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline font-medium flex items-center space-x-1"
                         >
                           <span>
                             {linkedCustomer.CustomerType === 'Company' 
                               ? (linkedCustomer.CompanyName || linkedCustomer.CustomerName)
                               : (linkedCustomer.PersonCompanyName || linkedCustomer.CompanyName)}
                           </span>
                           <ExternalLink className="h-3 w-3" />
                         </button>
                         <span className="text-xs text-blue-600 dark:text-blue-400">(Customer)</span>
                       </div>
                     ) : (
                       <span>{lead.CompanyName || '-'}</span>
                     )}
                   </div>
                 </div>
               )}

               {!isFieldHidden('Industry') && (
                 <div className="relative group">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center space-x-1">
                     <span>{t('leads.industry')}</span>
                     <div className="relative">
                       <HelpCircle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
                       <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg">
                         {t('leads.industryTooltip', 'The industry or business sector that the company operates in. This helps categorize and segment leads for better targeting.')}
                         <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                       </div>
                     </div>
                   </label>
                   <p className="text-sm text-gray-900 dark:text-white">
                     {linkedCustomer && (linkedCustomer.CustomerType === 'Company' || linkedCustomer.CompanyName || linkedCustomer.PersonCompanyName)
                       ? (linkedCustomer.Industry || linkedCustomer.PersonCompanyIndustry || lead.Industry || '-')
                       : lead.Industry || '-'}
                     {linkedCustomer && (linkedCustomer.Industry || linkedCustomer.PersonCompanyIndustry) && (
                       <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(from Customer)</span>
                     )}
                   </p>
                 </div>
               )}

               {!isFieldHidden('Website') && (
                 <div className="relative group">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center space-x-1">
                     <span>{t('leads.website')}</span>
                     <div className="relative">
                       <HelpCircle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
                       <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg">
                         {t('leads.websiteTooltip', 'The website URL of the company. Click to visit the website in a new tab. This provides additional information about the business.')}
                         <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                       </div>
                     </div>
                   </label>
                   {(() => {
                     const website = linkedCustomer && (linkedCustomer.CustomerType === 'Company' || linkedCustomer.CompanyName || linkedCustomer.PersonCompanyName)
                       ? (linkedCustomer.Website || linkedCustomer.PersonCompanyWebsite || lead.Website)
                       : lead.Website;
                     
                     return website ? (
                       <>
                         <a 
                           href={website.startsWith('http') ? website : `https://${website}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline flex items-center space-x-1"
                           title={`${t('leads.visitWebsite', 'Visit website')}: ${website}`}
                         >
                           <span>{website}</span>
                           <ExternalLink className="h-3 w-3" />
                         </a>
                         {linkedCustomer && (linkedCustomer.Website || linkedCustomer.PersonCompanyWebsite) && (
                           <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(Customer)</span>
                         )}
                       </>
                     ) : (
                       <p className="text-sm text-gray-900 dark:text-white">-</p>
                     );
                   })()}
                 </div>
               )}
             </div>
            </div>
          )}

          {/* Description and Notes */}
          {hasVisibleFieldsInGroup(['Description', 'Notes']) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium dark:text-white">{t('leads.descriptionNotes', 'Description & Notes')}</h3>
            </div>
            
            <div className="space-y-6">
              {!isFieldHidden('Description') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.description')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{lead.Description || '-'}</p>
                </div>
              )}
              
              {!isFieldHidden('Notes') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.notes')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{lead.Notes || '-'}</p>
                </div>
              )}

              {lead.Requirements && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.requirements')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{lead.Requirements}</p>
                </div>
              )}
            </div>
            </div>
          )}

          {customAttributeFields.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <CustomAttributesDisplay
                fields={customAttributeFields}
                attributes={leadAttributes}
                title={t('leads.additionalDataSection', 'Lead Additional Data')}
                entityName="lead"
                onFieldsUpdated={async () => {
                  if (propLead?.LeadID) {
                    window.location.reload();
                  }
                }}
              />
            </div>
          )}

          {/* Timeline */}
          {hasVisibleFieldsInGroup(['ExpectedCloseDate', 'LastContactDate', 'NextFollowUpDate', 'CreatedAt', 'UpdatedAt']) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium dark:text-white">{t('leads.timeline', 'Timeline')}</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!isFieldHidden('ExpectedCloseDate') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('leads.expectedCloseDate')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{formatDate(lead.ExpectedCloseDate)}</p>
                </div>
              )}

              {!isFieldHidden('LastContactDate') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('leads.lastContactDate')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{formatDate(lead.LastContactDate)}</p>
                </div>
              )}

              {!isFieldHidden('NextFollowUpDate') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('leads.nextFollowUpDate')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{formatDate(lead.NextFollowUpDate)}</p>
                </div>
              )}

              {!isFieldHidden('CreatedAt') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('leads.createdAt')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{formatDateTime(lead.CreatedAt)}</p>
                </div>
              )}
              
              {!isFieldHidden('UpdatedAt') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('leads.updatedAt')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{formatDateTime(lead.UpdatedAt)}</p>
                </div>
              )}
            </div>
            </div>
          )}

          {/* Related Activities via Entity Relations - shown in sidebar */}

          {/* Attachments */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Paperclip className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium dark:text-white">{t('leads.attachments', 'Attachments')}</h3>
            </div>
            <AttachmentServiceV2 
              entityType="Lead" 
              entityId={lead.LeadID} 
              onAttachmentChange={() => {
                // Optional: Refresh lead data or show notification
              }}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Entity Relations */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium dark:text-white">{t('common.relatedItems', 'Relationships')}</h3>
            </div>
            <div>
              {entityRelations.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('leads.noEntityRelations', 'No entity relations')}</p>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {(() => {
                    // Group entities by type
                    const grouped = entityRelations.reduce((acc, rel) => {
                      const type = rel.EntityType;
                      if (!acc[type]) {
                        acc[type] = [];
                      }
                      acc[type].push(rel);
                      return acc;
                    }, {});

                    // Define display order: Customer first, then Activity, then others
                    const typeOrder = ['Customer', 'Activity', 'Lead', 'Opportunity', 'SaleNote'];
                    const sortedTypes = Object.keys(grouped).sort((a, b) => {
                      const aIndex = typeOrder.indexOf(a);
                      const bIndex = typeOrder.indexOf(b);
                      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                      if (aIndex === -1) return 1;
                      if (bIndex === -1) return -1;
                      return aIndex - bIndex;
                    });

                    return sortedTypes.map((entityType) => {
                      const entities = grouped[entityType];
                      // For Customer type, sort by CustomerType (Company first, then Person)
                      const sortedEntities = entityType === 'Customer' 
                        ? [...entities].sort((a, b) => {
                            const aType = a.CustomerType || 'Company';
                            const bType = b.CustomerType || 'Company';
                            if (aType === bType) return 0;
                            return aType === 'Company' ? -1 : 1;
                          })
                        : entities;

                      return (
                        <div key={entityType} className="space-y-2">
                          <div className="flex items-center space-x-2 mb-2">
                            {getEntityIcon(entityType)}
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                              {entityType} ({entities.length})
                            </h4>
                          </div>
                          <div className="space-y-2">
                            {sortedEntities.map((rel, idx) => {
                              const colors = getEntityColors(rel.EntityType, rel.CustomerType);
                              const isPersonCustomer = rel.EntityType === 'Customer' && rel.CustomerType === 'Person';
                              
                              return (
                                <div
                                  key={rel.RelationID || `${rel.EntityType}-${rel.EntityID}-${idx}`}
                                  className={`flex items-center space-x-3 p-3 rounded-lg border ${colors.border} ${colors.bg} ${colors.hoverBg} cursor-pointer transition-all ${
                                    isPersonCustomer ? 'ml-6' : ''
                                  }`}
                                  onClick={() => {
                                    const path = getEntityPath(rel.EntityType, rel.EntityID);
                                    if (path !== '#') navigate(path);
                                  }}
                                >
                                  {getEntityIcon(rel.EntityType)}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{rel.EntityName}</p>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${colors.badge}`}>
                                        {rel.EntityType}
                                        {rel.CustomerType && ` (${rel.CustomerType === 'Person' ? 'Individual' : 'Company'})`}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">ID: {rel.EntityID}</span>
                                    </div>
                                  </div>
                                  <ArrowRight className={`h-4 w-4 ${colors.icon} flex-shrink-0`} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
          {/* Scoring & Qualification */}
          {hasVisibleFieldsInGroup(['Score', 'QualificationLevel']) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Star className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium dark:text-white">{t('leads.scoringQualification', 'Scoring & Qualification')}</h3>
              </div>
              
              <div className="space-y-4">
              {!isFieldHidden('Score') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.score')} (0-100)
                  </label>
                  <div className="text-center">
                    <div 
                      className={`relative text-3xl font-bold ${getScoreColor(lead.Score)} cursor-help inline-block`}
                      onMouseEnter={() => setShowScoreTooltip(true)}
                      onMouseLeave={() => setShowScoreTooltip(false)}
                    >
                      {lead.Score || 0}
                      
                      {/* Tooltip */}
                      {showScoreTooltip && (
                        <div className="absolute z-50 w-80 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl top-full left-1/2 transform -translate-x-1/2 mt-2"
                             onClick={(e) => e.stopPropagation()}>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                              <h5 className="font-semibold text-sm text-gray-900 dark:text-white">
                                เหตุผลการให้คะแนน
                              </h5>
                              <span className={`text-xl font-bold ${getScoreColor(lead.Score)}`}>
                                {lead.Score || 0}%
                              </span>
                            </div>
                            
                            <div className="text-xs">
                              <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {getScoreReasons(lead).summary}
                              </p>
                              
                              <div className="space-y-1.5">
                                {getScoreReasons(lead).reasons.map((reason, idx) => (
                                  <div key={idx} className="flex items-start space-x-1">
                                    <span className="text-gray-600 dark:text-gray-400 leading-tight">
                                      {reason}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              
                              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                                <p className="text-xs text-gray-500 dark:text-gray-500 italic">
                                  💡 คะแนนคำนวณจากความครบถ้วนและคุณภาพของข้อมูล
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${lead.Score || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              {!isFieldHidden('QualificationLevel') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.qualificationLevel')}
                  </label>
                  <div className="text-center">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getQualificationColor(lead.QualificationLevel)}`}>
                      {lead.QualificationLevel || t('leads.notSet')}
                    </span>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Financial Information */}
          {hasVisibleFieldsInGroup(['EstimatedBudget', 'Currency']) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium dark:text-white">{t('leads.financialInfo', 'Financial Information')}</h3>
            </div>
            
            <div className="space-y-4">
              {!isFieldHidden('EstimatedBudget') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.estimatedBudget')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {lead.EstimatedBudget ? formatCurrencyLocal(lead.EstimatedBudget, lead.Currency) : '-'}
                  </p>
                </div>
              )}

              {!isFieldHidden('Currency') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.currency')}
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">{lead.Currency || '-'}</p>
                </div>
              )}
            </div>
            </div>
          )}

          {/* Conversion Status */}
          {hasVisibleFieldsInGroup(['ConvertedToOpportunity', 'ConvertedToCustomer', 'ConversionDate']) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Award className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium dark:text-white">{t('leads.conversionStatus', 'Conversion Status')}</h3>
              </div>
              
              <div className="space-y-4">
              {!isFieldHidden('ConvertedToOpportunity') && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="viewConvertedToOpportunity"
                    checked={lead.ConvertedToOpportunity || false}
                    disabled
                    className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                  />
                  <label htmlFor="viewConvertedToOpportunity" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                    {t('leads.convertedToOpportunity')}
                  </label>
                </div>
              )}

              {!isFieldHidden('ConvertedToCustomer') && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="viewConvertedToCustomer"
                    checked={lead.ConvertedToCustomer || false}
                    disabled
                    className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                  />
                  <label htmlFor="viewConvertedToCustomer" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                    {t('leads.convertedToCustomer')}
                  </label>
                </div>
              )}

              {!isFieldHidden('ConversionDate') && lead.ConversionDate && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('leads.conversionDateLabel')}:</span>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(lead.ConversionDate)}</p>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Tags Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Tag className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium dark:text-white">{t('common.tags', 'Tags')}</h3>
            </div>
            <TagService
              entityType="Lead"
              entityId={lead.LeadID}
              initialTags={lead.Tags ? (typeof lead.Tags === 'string' ? lead.Tags.split(',').map(t => t.trim()).filter(Boolean) : lead.Tags) : []}
              maxTags={15}
              onChange={async (newTags) => {
                // Refresh lead data after tag change
                await fetchLeadDetails(lead.LeadID);
              }}
            />
          </div>
        </div>
      </div>

      {/* Add to Customer Modal */}
      {showAddCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">
                  {customerCheck.duplicates.length > 0 
                    ? t('leads.duplicateCustomersFound', 'Duplicate Customers Found')
                    : lead.ConvertedToCustomer 
                      ? t('leads.recreateCustomer', 'Recreate Customer')
                      : t('leads.addToCustomers', 'Add to Customers')
                  }
                </h2>
                <button
                  onClick={() => {
                    setShowAddCustomerModal(false);
                    setShowDataExtraction(false);
                    setShowNameMapping(false);
                    setNameMapping(null);
                    setExtractedData(null);
                    setCustomerType('Person');
                    setSelectedData({
                      address: '',
                      phone: '',
                      email: '',
                      website: '',
                      location: '',
                      industry: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-200 transition-colors"
                  disabled={addingCustomer}
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {showDataExtraction ? (
                <div className="space-y-6">
                  <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      🤖 Smart Data Extraction
                    </h3>
                    <p className="text-sm text-blue-200">
                      AI has analyzed the lead's description and notes to extract additional information. Please review and select customer type.
                    </p>
                    <div className="mt-2 text-xs text-blue-300">
                      Debug: addingCustomer = {addingCustomer ? 'true' : 'false'} | Customer Type = {customerType}
                    </div>
                  </div>

                  {/* Customer Type Selection */}
                  <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                    <h4 className="font-medium text-gray-300 mb-3">Customer Type</h4>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="customerType"
                          value="Person"
                          checked={customerType === 'Person'}
                          onChange={(e) => setCustomerType(e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-white">Individual Person</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="customerType"
                          value="Company"
                          checked={customerType === 'Company'}
                          onChange={(e) => setCustomerType(e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-white">Company</span>
                      </label>
                    </div>
                    
                    {/* Show company information if available */}
                    {lead.CompanyName && (
                      <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700/30 rounded">
                        <h5 className="font-medium text-blue-200 mb-2">Company Information Available:</h5>
                        <div className="text-sm text-blue-100">
                          <p><strong>Company Name:</strong> {lead.CompanyName}</p>
                          {lead.Industry && <p><strong>Industry:</strong> {lead.Industry}</p>}
                          {lead.Website && <p><strong>Website:</strong> {lead.Website}</p>}
                          {lead.Location && <p><strong>Location:</strong> {lead.Location}</p>}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Extracted Data Display */}
                  <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                    <h4 className="font-medium text-gray-300 mb-3">Select and Edit Information</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {/* Addresses */}
                      {extractedData?.addresses.length > 0 && (
                        <div>
                          <label className="font-medium text-gray-300 mb-2">Address:</label>
                          <select
                            value={selectedData.address}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, address: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2 mb-2"
                          >
                            <option value="">Select address...</option>
                            {extractedData.addresses.map((address, idx) => (
                              <option key={idx} value={address}>{address}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={selectedData.address}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="Or type custom address..."
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                          />
                        </div>
                      )}

                      {/* Phones */}
                      {extractedData?.phones.length > 0 && (
                        <div>
                          <label className="font-medium text-gray-300 mb-2">Phone:</label>
                          <select
                            value={selectedData.phone}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, phone: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2 mb-2"
                          >
                            <option value="">Select phone...</option>
                            {extractedData.phones.map((phone, idx) => (
                              <option key={idx} value={phone}>{phone}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={selectedData.phone}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="Or type custom phone..."
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                          />
                        </div>
                      )}

                      {/* Emails */}
                      {extractedData?.emails.length > 0 && (
                        <div>
                          <label className="font-medium text-gray-300 mb-2">Email:</label>
                          <select
                            value={selectedData.email}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2 mb-2"
                          >
                            <option value="">Select email...</option>
                            {extractedData.emails.map((email, idx) => (
                              <option key={idx} value={email}>{email}</option>
                            ))}
                          </select>
                          <input
                            type="email"
                            value={selectedData.email}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="Or type custom email..."
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                          />
                        </div>
                      )}

                      {/* Websites */}
                      {extractedData?.websites.length > 0 && (
                        <div>
                          <label className="font-medium text-gray-300 mb-2">Website:</label>
                          <select
                            value={selectedData.website}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, website: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2 mb-2"
                          >
                            <option value="">Select website...</option>
                            {extractedData.websites.map((website, idx) => (
                              <option key={idx} value={website}>{website}</option>
                            ))}
                          </select>
                          <input
                            type="url"
                            value={selectedData.website}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, website: e.target.value }))}
                            placeholder="Or type custom website..."
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                          />
                        </div>
                      )}

                      {/* Locations */}
                      {extractedData?.locations.length > 0 && (
                        <div>
                          <label className="font-medium text-gray-300 mb-2">Location:</label>
                          <select
                            value={selectedData.location}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, location: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2 mb-2"
                          >
                            <option value="">Select location...</option>
                            {extractedData.locations.map((location, idx) => (
                              <option key={idx} value={location}>{location}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={selectedData.location}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, location: e.target.value }))}
                            placeholder="Or type custom location..."
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                          />
                        </div>
                      )}

                      {/* Industries */}
                      {extractedData?.industries.length > 0 && (
                        <div>
                          <label className="font-medium text-gray-300 mb-2">Industry:</label>
                          <select
                            value={selectedData.industry}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, industry: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2 mb-2"
                          >
                            <option value="">Select industry...</option>
                            {extractedData.industries.map((industry, idx) => (
                              <option key={idx} value={industry}>{industry}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={selectedData.industry}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, industry: e.target.value }))}
                            placeholder="Or type custom industry..."
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                          />
                        </div>
                      )}

                    </div>

                    {/* Selected Data Summary */}
                    <div className="mt-6 bg-green-900/30 border border-green-700/50 rounded-lg p-4">
                      <h5 className="font-medium text-green-200 mb-3">Selected Data Summary:</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {selectedData.address && (
                          <div className="flex items-center space-x-2">
                            <span className="text-green-300">📍</span>
                            <span className="text-white">{selectedData.address}</span>
                          </div>
                        )}
                        {selectedData.phone && (
                          <div className="flex items-center space-x-2">
                            <span className="text-green-300">📞</span>
                            <span className="text-white">{selectedData.phone}</span>
                          </div>
                        )}
                        {selectedData.email && (
                          <div className="flex items-center space-x-2">
                            <span className="text-green-300">📧</span>
                            <span className="text-white">{selectedData.email}</span>
                          </div>
                        )}
                        {selectedData.website && (
                          <div className="flex items-center space-x-2">
                            <span className="text-green-300">🌐</span>
                            <span className="text-white">{selectedData.website}</span>
                          </div>
                        )}
                        {selectedData.location && (
                          <div className="flex items-center space-x-2">
                            <span className="text-green-300">📍</span>
                            <span className="text-white">{selectedData.location}</span>
                          </div>
                        )}
                        {selectedData.industry && (
                          <div className="flex items-center space-x-2">
                            <span className="text-green-300">🏢</span>
                            <span className="text-white">{selectedData.industry}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Validation Results */}
                    {extractedData?.validation && (
                      <div className="mt-4">
                        {extractedData.validation.errors.length > 0 && (
                          <div className="bg-red-900/30 border border-red-700/50 rounded p-3 mb-2">
                            <h5 className="font-medium text-red-200 mb-1">Errors:</h5>
                            {extractedData.validation.errors.map((error, idx) => (
                              <p key={idx} className="text-red-300 text-xs">{error}</p>
                            ))}
                          </div>
                        )}
                        
                        {extractedData.validation.warnings.length > 0 && (
                          <div className="bg-yellow-900/30 border border-yellow-700/50 rounded p-3">
                            <h5 className="font-medium text-yellow-200 mb-1">Warnings:</h5>
                            {extractedData.validation.warnings.map((warning, idx) => (
                              <p key={idx} className="text-yellow-300 text-xs">{warning}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        console.log('🔍 Continue to Name Mapping clicked');
                        setShowNameMapping(true);
                        setShowDataExtraction(false);
                      }}
                      disabled={addingCustomer}
                      className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                    >
                      Continue to Name Mapping
                    </button>
                    <button
                      onClick={() => {
                        console.log('🔍 Cancel clicked');
                        setShowDataExtraction(false);
                      }}
                      disabled={addingCustomer}
                      className="px-4 py-3 border border-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : showNameMapping ? (
                <div className="space-y-6">
                  <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      {customerType === 'Company' ? 'Company Information Mapping' : 'Name Mapping Preview'}
                    </h3>
                    <p className="text-sm text-blue-200">
                      {customerType === 'Company' 
                        ? 'Please review and edit company information for customer creation:'
                        : 'Please review how the Thai name will be parsed into customer fields:'
                      }
                    </p>
                  </div>

                  <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                    {customerType === 'Company' ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="font-medium text-gray-300">Company Name</label>
                          <input
                            type="text"
                            value={lead.CompanyName || ''}
                            onChange={(e) => setLead(prev => ({ ...prev, CompanyName: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                            placeholder="Enter company name..."
                          />
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">Industry</label>
                          <input
                            type="text"
                            value={selectedData.industry || lead.Industry || ''}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, industry: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                            placeholder="Enter industry..."
                          />
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">Location</label>
                          <input
                            type="text"
                            value={selectedData.location || ''}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, location: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                            placeholder="Enter location..."
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="font-medium text-gray-300">Website</label>
                          <input
                            type="url"
                            value={selectedData.website || lead.Website || ''}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, website: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                            placeholder="Enter website URL..."
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="font-medium text-gray-300">Address</label>
                          <input
                            type="text"
                            value={selectedData.address || ''}
                            onChange={(e) => setSelectedData(prev => ({ ...prev, address: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                            placeholder="Enter company address..."
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="font-medium text-gray-300">{safeT('leads.originalName', 'Original Name')}</label>
                          <p className="text-white text-lg font-medium">{lead.ContactName}</p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">{safeT('leads.title', 'Title')}</label>
                          <p className="text-white">{nameMapping?.title || '-'}</p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">{safeT('leads.firstName', 'First Name')}</label>
                          <input
                            type="text"
                            value={nameMapping?.firstName || ''}
                            onChange={(e) => setNameMapping(prev => ({ ...prev, firstName: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">{safeT('leads.lastName', 'Last Name')}</label>
                          <input
                            type="text"
                            value={nameMapping?.lastName || ''}
                            onChange={(e) => setNameMapping(prev => ({ ...prev, lastName: e.target.value }))}
                            className="w-full bg-gray-600 text-white border border-gray-500 rounded px-3 py-2"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={handleConfirmCustomerCreation}
                      disabled={addingCustomer}
                      className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                    >
                      {addingCustomer ? safeT('common.processing', 'Processing...') : 
                        customerType === 'Company' ? 'Confirm & Create Company' : safeT('leads.confirmCreateCustomer', 'Confirm & Create Customer')
                      }
                    </button>
                    <button
                      onClick={() => setShowNameMapping(false)}
                      disabled={addingCustomer}
                      className="px-4 py-3 border border-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 hover:text-white transition-colors"
                    >
                      {safeT('common.cancel', 'Cancel')}
                    </button>
                  </div>
                  
                  {/* Error Message Display */}
                  {error && (
                    <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4">
                      <h5 className="font-medium text-red-200 mb-2">Error:</h5>
                      <p className="text-red-300 text-sm">{error}</p>
                    </div>
                  )}
                </div>
              ) : customerCheck.duplicates.length > 0 ? (
                <div className="space-y-6">
                  <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4">
                    <h4 className="text-base font-semibold text-yellow-100 mb-2">
                      {safeT('leads.duplicateFoundTitle', '⚠️ Similar Customer Found')}
                    </h4>
                    <p className="text-sm text-yellow-200 mb-2">
                      {safeT('leads.duplicateWarning', 'This contact has not been saved as a customer yet, but we found existing customers with matching information (name, email, or phone).')}
                    </p>
                    <p className="text-sm text-yellow-200">
                      {safeT('leads.duplicateWarningAction', 'If this is the same person, click "Merge with This" to link this lead to the existing customer. Otherwise, scroll down to create a new customer record.')}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-white">
                      {safeT('leads.existingCustomersMatched', 'Matching Customers Found')}
                    </h3>
                    
                    {customerCheck.duplicates.map((duplicate) => (
                      <div 
                        key={duplicate.CustomerID} 
                        className="border border-gray-600 rounded-lg p-4 hover:border-blue-500 transition-colors bg-gray-700/50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="flex items-center space-x-2">
                                <User className="h-5 w-5 text-gray-400" />
                                <span className="font-semibold text-white">
                                  {duplicate.FirstName} {duplicate.LastName}
                                </span>
                              </div>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                duplicate.matchType === 'email' ? 'bg-red-900/50 text-red-200 border border-red-700' :
                                duplicate.matchType === 'phone' ? 'bg-orange-900/50 text-orange-200 border border-orange-700' :
                                'bg-blue-900/50 text-blue-200 border border-blue-700'
                              }`}>
                                {t(`leads.match_${duplicate.matchType}`, `Match: ${duplicate.matchType}`)}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-300 mb-3">
                              {duplicate.PersonEmail && (
                                <div className="flex items-center space-x-2">
                                  <Mail className="h-4 w-4" />
                                  <a 
                                    href={`mailto:${duplicate.PersonEmail}`}
                                    className="text-blue-400 hover:text-blue-300 hover:underline"
                                    title={`Send email to ${duplicate.PersonEmail}`}
                                  >
                                    {duplicate.PersonEmail}
                                  </a>
                                </div>
                              )}
                              {duplicate.PersonPhone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="h-4 w-4" />
                                  <span>{duplicate.PersonPhone}</span>
                                </div>
                              )}
                              {duplicate.CompanyName && (
                                <div className="flex items-center space-x-2">
                                  <Building className="h-4 w-4" />
                                  <span>{duplicate.CompanyName}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* System Information Section */}
                            {duplicate.CustomerCode && (
                              <div className="mt-3 pt-3 border-t border-gray-600">
                                <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                                  {t('common.systemInformation', 'System Information')}
                                </h5>
                                <div className="flex items-center space-x-2 text-sm text-gray-300">
                                  <FileText className="h-4 w-4" />
                                  <span className="text-gray-400">Customer Code:</span>
                                  <span className="font-mono text-gray-200">{duplicate.CustomerCode}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <button
                            onClick={() => handleMergeWithExisting(duplicate.CustomerID)}
                            disabled={addingCustomer}
                            className="ml-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors whitespace-nowrap"
                          >
                            {addingCustomer ? safeT('common.processing', 'Processing...') : safeT('leads.mergeWithThisCustomer', 'Link to This Customer')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-600 pt-6">
                    <h3 className="font-semibold text-lg text-white mb-3">
                      {safeT('leads.orCreateNew', 'Or Create as New Customer')}
                    </h3>
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-200">
                        {safeT('leads.createNewDescription', 'If this contact is a different person from the customers shown above, you can create a completely new customer record. The lead information will be used to populate the new customer.')}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        console.log('🔍 Create New Customer clicked, addingCustomer:', addingCustomer);
                        handleCreateNewCustomer();
                      }}
                      disabled={addingCustomer}
                      className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                    >
                      {addingCustomer ? safeT('common.processing', 'Processing...') : safeT('leads.createNewCustomerAnyway', 'Create as New Customer (Not a Duplicate)')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-4">
                    <h4 className="text-base font-semibold text-green-100 mb-2">
                      {lead.ConvertedToCustomer 
                        ? safeT('leads.recreateCustomerTitle', '🔄 Recreate Customer Record')
                        : safeT('leads.readyToCreateTitle', '✅ Ready to Create New Customer')
                      }
                    </h4>
                    <p className="text-sm text-green-200">
                      {lead.ConvertedToCustomer 
                        ? safeT('leads.recreateCustomerDescription', 'This lead was previously converted to a customer, but the customer record no longer exists. You can recreate the customer record using the information below.')
                        : safeT('leads.noDuplicatesFound', 'Good news! We checked your customer database and found no duplicates. This contact can be safely added as a new customer. The lead information shown below will be used to create the customer record.')
                      }
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-white">
                      {safeT('leads.contactDetailsToConvert', 'Contact Details to Convert')}
                    </h3>
                    
                    <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label className="font-medium text-gray-300">{safeT('leads.contactName', 'Contact Name')}</label>
                          <p className="text-white">{lead.ContactName || '-'}</p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">{safeT('leads.jobTitle', 'Job Title')}</label>
                          <p className="text-white">{lead.JobTitle || lead.Title || '-'}</p>
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">{safeT('leads.email', 'Email')}</label>
                          {lead.ContactEmail ? (
                            <a 
                              href={`mailto:${lead.ContactEmail}`}
                              className="text-blue-400 hover:text-blue-300 hover:underline"
                              title={`Send email to ${lead.ContactEmail}`}
                            >
                              {lead.ContactEmail}
                            </a>
                          ) : (
                            <p className="text-white">-</p>
                          )}
                        </div>
                        <div>
                          <label className="font-medium text-gray-300">{safeT('leads.phone', 'Phone')}</label>
                          <p className="text-white">{lead.ContactPhone || '-'}</p>
                        </div>
                        {lead.CompanyName && (
                          <>
                            <div>
                              <label className="font-medium text-gray-300">{safeT('leads.companyName', 'Company Name')}</label>
                              <p className="text-white">{lead.CompanyName}</p>
                            </div>
                            <div>
                              <label className="font-medium text-gray-300">{safeT('leads.industry', 'Industry')}</label>
                              <p className="text-white">{lead.Industry || '-'}</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      console.log('🔍 Create Customer clicked, addingCustomer:', addingCustomer);
                      handleCreateNewCustomer();
                    }}
                    disabled={addingCustomer}
                    className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
                  >
                    {addingCustomer ? safeT('common.processing', 'Processing...') : 
                      lead.ConvertedToCustomer 
                        ? safeT('leads.recreateCustomer', 'Recreate Customer Record')
                        : safeT('leads.createCustomerFromLead', 'Create New Customer from This Lead')
                    }
                  </button>
                </div>
              )}
            </div>

            {!showNameMapping && !showDataExtraction && (
              <div className="p-6 border-t border-gray-700 bg-gray-900/50">
                <button
                  onClick={() => {
                    setShowAddCustomerModal(false);
                    setShowNameMapping(false);
                    setNameMapping(null);
                    setExtractedData(null);
                    setCustomerType('Person');
                    setSelectedData({
                      address: '',
                      phone: '',
                      email: '',
                      website: '',
                      location: '',
                      industry: ''
                    });
                  }}
                  disabled={addingCustomer}
                  className="w-full px-4 py-2 border border-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 hover:text-white transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lead to Opportunity Conversion Modal */}
      {showConversionModal && lead && (
        <LeadToOpportunityModal
          lead={lead}
          onSubmit={handleConversionSubmit}
          onCancel={() => setShowConversionModal(false)}
        />
      )}
    </div>
  );
};

export default LeadDetail; 
